import { useGetAttachmentsByDocId } from "@/api/attachment";
import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import { Accordion } from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { SupportedLocale } from "@/i18n";
import { DimensionsFormatting } from "@/lib/compute-field/shared/dimensions";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import {
  computeFieldCollectorNameForOverview,
  computeFieldRelatedAuctions,
  computeFieldRelatedAuthorsArtwork,
  computeFieldRelatedBibliographies,
  computeFieldRelatedExhibitions,
  computeFieldTirageHistory,
} from "@/lib/computeFields";
import { DimensionsDTO } from "@/lib/shared-types";
import {
  mapTirageInAuctionsToArtwork,
  mapTirageInBibliographiesToArtwork,
  mapTirageInExhibitionsToArtwork,
} from "@/lib/tirage-relation-mappers";
import { parseJsonOrUndefined } from "@/lib/utils";
import { useMemo } from "react";
import { MergedDisplayData } from "../types";
import { AuctionsAccordion } from "./AuctionsAccordion";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { HistoryAccordion } from "./HistoryAccordion";

interface TirageDrawerProps {
  tirage: any;
  displayData: MergedDisplayData;
  locale: SupportedLocale;
  t: (key: string) => string;
  trigger: React.ReactNode;
  isTirage: boolean;
}

export function TirageDrawer({
  tirage,
  displayData,
  locale,
  t,
  trigger,
}: TirageDrawerProps) {
  const { data: attachments } = useGetAttachmentsByDocId(tirage.id);

  // Compute author from master artwork
  const author = useMemo(() => {
    return computeFieldRelatedAuthorsArtwork(displayData, {
      language: locale,
    });
  }, [displayData, locale]);

  // Parse dimensions
  const dimensionsWithMargins = useMemo(() => {
    if (!tirage?.tirageDescriptionDimensionsWithMargins) return null;
    return typeof tirage.tirageDescriptionDimensionsWithMargins === "string"
      ? parseJsonOrUndefined<DimensionsDTO>(
          tirage.tirageDescriptionDimensionsWithMargins,
        )
      : tirage.tirageDescriptionDimensionsWithMargins;
  }, [tirage?.tirageDescriptionDimensionsWithMargins]);

  const dimensionsWithoutMargins = useMemo(() => {
    if (!tirage?.tirageDescriptionDimensionsWithoutMargins) return null;
    return typeof tirage.tirageDescriptionDimensionsWithoutMargins === "string"
      ? parseJsonOrUndefined<DimensionsDTO>(
          tirage.tirageDescriptionDimensionsWithoutMargins,
        )
      : tirage.tirageDescriptionDimensionsWithoutMargins;
  }, [tirage?.tirageDescriptionDimensionsWithoutMargins]);

  // Helper to get multilingual field value
  const getMultilingualField = (
    field: Record<SupportedLocale, string> | undefined,
  ): string | null => {
    if (!field) return null;
    return field[locale] || null;
  };

  // Compute tirage history
  const tirageHistoryCalculated = useMemo(() => {
    if (!tirage.tirageHistory || tirage.tirageHistory.length === 0) {
      return null;
    }
    return computeFieldTirageHistory(
      { tirageHistory: tirage.tirageHistory },
      { language: locale, translation: t, lineBreak: true },
    );
  }, [tirage.tirageHistory, locale, t]);

  // Compute fields for tirage-specific relations
  // Use centralized mappers for consistency and maintainability
  const tirageExhibitionsCalculated = useMemo(() => {
    const adaptedExhibitions = mapTirageInExhibitionsToArtwork(
      tirage.tirageInExhibition,
    );
    if (!adaptedExhibitions) {
      return null;
    }
    return computeFieldRelatedExhibitions(
      { relatedExhbitions: adaptedExhibitions },
      { language: locale },
    );
  }, [tirage.tirageInExhibition, locale]);

  const tirageAuctionsCalculated = useMemo(() => {
    const adaptedAuctions = mapTirageInAuctionsToArtwork(
      tirage.tirageInAuction,
    );
    if (!adaptedAuctions) {
      return null;
    }
    return computeFieldRelatedAuctions(
      { relatedAuctions: adaptedAuctions },
      { language: locale },
    );
  }, [tirage.tirageInAuction, locale]);

  const tirageBibliographyCalculated = useMemo(() => {
    const adaptedBibliographies = mapTirageInBibliographiesToArtwork(
      tirage.tirageInBibliography,
    );
    if (!adaptedBibliographies) {
      return null;
    }
    return computeFieldRelatedBibliographies(
      { relatedBibliographies: adaptedBibliographies },
      { language: locale },
    );
  }, [tirage.tirageInBibliography, locale]);

  return (
    <Sheet>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto pb-8"
      >
        <SheetHeader>
          <SheetTitle className="text-center">
            {tirage.tirageDescriptionNumeroEnregistrement}
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col items-center gap-4 mt-6">
          {tirage && (
            <>
              {/* Image */}
              {attachments && attachments.length > 0 && (
                <div className="w-full max-w-md flex justify-center">
                  <ImageFullscreen
                    media={{
                      attachments: attachments,
                      type: "tirage",
                      alt:
                        tirage.tirageDescriptionNumeroEnregistrement ||
                        "Tirage",
                      width: 600,
                      height: 600,
                      className: "object-contain max-h-[400px] w-auto",
                    }}
                  />
                </div>
              )}

              {/* Author */}
              {author && (
                <p className="font-bold text-[15px] text-center w-full">
                  {t("tirageDescriptionAuteurLabelFront") + " "}
                  <span dangerouslySetInnerHTML={{ __html: author }} />
                </p>
              )}

              {/* Registration number */}
              {tirage.tirageDescriptionNumeroEnregistrement && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionNumeroEnregistrementLabelFront") + " "}
                  {tirage.tirageDescriptionNumeroEnregistrement}
                </p>
              )}

              {/* Inventory number */}
              {tirage.tirageDescriptionNumeroInventaire && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionNumeroInventaireLabelFront") + " "}
                  {tirage.tirageDescriptionNumeroInventaire}
                </p>
              )}

              {/* Date of creation */}
              {tirage.tirageDescriptionDateCreation && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionDateCreationLabelFront") + " "}
                  {tirage.tirageDescriptionDateCreation}
                </p>
              )}

              {/* Type */}
              {getMultilingualField(tirage.tirageDescriptionType) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionTypeLabelFront") + " "}
                  {getMultilingualField(tirage.tirageDescriptionType)}
                </p>
              )}

              {/* Printer */}
              {getMultilingualField(tirage.tirageDescriptionPrinter) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionPrinterLabelFront") + " "}
                  {getMultilingualField(tirage.tirageDescriptionPrinter)}
                </p>
              )}

              {/* Technique and Support */}
              {(tirage.tirageDescriptionTechnique?.[locale] ||
                tirage.tirageDescriptionSupport?.[locale]) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionTechniqueSupportLabelFront") + " "}
                  <SupportTechniqueCF
                    input={{
                      technique: tirage.tirageDescriptionTechnique || {},
                      support: tirage.tirageDescriptionSupport || {},
                      preposition: t(
                        "computeField.techniqueSupportPreposition",
                      ),
                    }}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {/* Edition number */}
              {getMultilingualField(tirage.tirageDescriptionEditionNumber) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionEditionNumberLabelFront") + " "}
                  {getMultilingualField(tirage.tirageDescriptionEditionNumber)}
                </p>
              )}

              {/* Signature/Inscription */}
              {getMultilingualField(
                tirage.tirageDescriptionSignatureInscription,
              ) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionSignatureInscriptionLabelFront") + " "}
                  {getMultilingualField(
                    tirage.tirageDescriptionSignatureInscription,
                  )}
                </p>
              )}

              {/* Description de la signature du dos */}
              {getMultilingualField(
                tirage.tirageDescriptionSignatureInscriptionDos,
              ) &&
                tirage.tirageDescriptionSignatureInscriptionDosPublished?.[
                  locale
                ] && (
                  <p className="font-normal text-sm text-center w-full">
                    {t("tirageDescriptionSignatureInscriptionDosLabelFront") +
                      " "}
                    {getMultilingualField(
                      tirage.tirageDescriptionSignatureInscriptionDos,
                    )}
                  </p>
                )}

              {/* Dimensions with margins */}
              {dimensionsWithMargins && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionDimensionsWithMarginsLabelFront") + " "}
                  <DimensionsFormatting
                    input={dimensionsWithMargins}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {/* Dimensions without margins */}
              {dimensionsWithoutMargins && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionDimensionsWithoutMarginsLabelFront") +
                    " "}
                  <DimensionsFormatting
                    input={dimensionsWithoutMargins}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {/* Collector */}
              {tirage.collector && typeof tirage.collector === "object" && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionCollectorLabelFront") + " "}
                  {computeFieldCollectorNameForOverview(
                    {
                      collectionneursDescriptionNom:
                        tirage.collector.collectionneursDescriptionNom,
                      collectionneursDescriptionPrenom:
                        tirage.collector.collectionneursDescriptionPrenom,
                      collectionneursDescriptionCollectionPublique:
                        tirage.collector
                          .collectionneursDescriptionCollectionPublique,
                      collectionneursDescriptionVille:
                        tirage.collector.collectionneursDescriptionVille,
                      showCity: true,
                    },
                    { language: locale, translation: t },
                  )}
                </p>
              )}

              {/* Credit */}
              {displayData.oeuvresCreditsCreditOeuvre && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionCreditLabelFront") + " "}
                  {displayData.oeuvresCreditsCreditOeuvre}
                </p>
              )}

              {/* Accordions */}
              {(tirage.tirageHistory && tirage.tirageHistory.length > 0) ||
              (tirage.tirageInExhibition &&
                tirage.tirageInExhibition.length > 0) ||
              (tirage.tirageInBibliography &&
                tirage.tirageInBibliography.length > 0) ||
              (tirage.tirageInAuction && tirage.tirageInAuction.length > 0) ? (
                <div className="w-3/4 mt-6 mb-8">
                  <Accordion
                    type="single"
                    collapsible
                    className="font-medium text-base text-gray-800"
                  >
                    {tirage.tirageHistory &&
                      tirage.tirageHistory.length > 0 &&
                      tirageHistoryCalculated && (
                        <HistoryAccordion
                          historyCalculated={null}
                          tirageHistoryCalculated={tirageHistoryCalculated}
                          t={t}
                        />
                      )}
                    {tirage.tirageInExhibition &&
                      tirage.tirageInExhibition.length > 0 &&
                      tirageExhibitionsCalculated && (
                        <ExhibitionsAccordion
                          exhibitionsCalculated={tirageExhibitionsCalculated}
                          t={t}
                        />
                      )}
                    {tirage.tirageInBibliography &&
                      tirage.tirageInBibliography.length > 0 &&
                      tirageBibliographyCalculated && (
                        <BibliographyAccordion
                          bibliographyCalculated={tirageBibliographyCalculated}
                          t={t}
                        />
                      )}
                    {tirage.tirageInAuction &&
                      tirage.tirageInAuction.length > 0 &&
                      tirageAuctionsCalculated && (
                        <AuctionsAccordion
                          auctionCalculated={tirageAuctionsCalculated}
                          t={t}
                        />
                      )}
                  </Accordion>
                </div>
              ) : null}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
